import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { CommunicationType } from "@/enums/communicationLogs";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import {
  INBOX_SHORTCUT_NEXT_RECORD,
  INBOX_SHORTCUT_PREVIOUS_RECORD,
  INBOX_SHORTCUT_REPLY,
} from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { ROUTE_DEAL_ACTIVITY, ROUTE_INBOX } from "@/router/routes";

import type { CommunicationLogBase } from "@/models/communicationLogs";
import type { Ref } from "vue";

interface UseInboxKeyboardShortcutsOptions {
  allDataPages: Ref<CommunicationLogBase[] | undefined>;
  isOpen: Ref<boolean>;
  focusedIndex: Ref<number>;
  toggleThread: (id: number, target?: "data") => void;
  closeThread: () => void;
  incrementThread: (step: number) => void;
  scrollTo: (index: number) => void;
}

export const useInboxKeyboardShortcuts = ({
  allDataPages,
  isOpen,
  focusedIndex,
  toggleThread,
  closeThread,
  incrementThread,
  scrollTo,
}: UseInboxKeyboardShortcutsOptions) => {
  const route = useRoute();
  const router = useRouter();
  const emitter = useEmitter();
  const { assignShortcutToAction } = useKeyboardShortcuts();

  const isActivitiesRoute = computed(
    () => route.name === ROUTE_INBOX || route.name === ROUTE_DEAL_ACTIVITY
  );

  const isActiveElementEditable = (activeElement: Element | null) => {
    if (!activeElement) {
      return false;
    }
    const tagName = activeElement.tagName.toLowerCase();
    return (
      tagName === "input" ||
      tagName === "textarea" ||
      activeElement.getAttribute("contenteditable") === "true"
    );
  };

  const canPerformInboxAction = () => {
    if (!isActivitiesRoute.value) {
      return false;
    }

    const activeElement = document.activeElement;

    if (isActiveElementEditable(activeElement)) {
      return false;
    }

    const dialog = document.querySelector('[role="dialog"]');
    if (dialog) {
      return false;
    }

    if (activeElement?.closest("[data-keyboard-nav]")) {
      return false;
    }

    return true;
  };

  const canPerformEscapeAction = () => {
    if (!isActivitiesRoute.value) {
      return false;
    }

    const dialog = document.querySelector('[role="dialog"]');
    if (dialog) {
      return false;
    }

    return true;
  };

  const navigateToTab = (type?: CommunicationType) => {
    const routeName = route.name as string;
    const isInbox = routeName === ROUTE_INBOX;

    const query: Record<string, string> = {};
    if (type) {
      query.type = type;
    }
    if (isInbox && type !== CommunicationType.note) {
      query.view = "inbox";
    }

    router.push({ name: routeName, query });
  };

  const jumpToTop = () => {
    if (!allDataPages.value?.length) {
      return;
    }
    focusedIndex.value = 0;
    scrollTo(0);
  };

  const jumpToBottom = () => {
    if (!allDataPages.value?.length) {
      return;
    }
    const lastIndex = allDataPages.value.length - 1;
    focusedIndex.value = lastIndex;
    scrollTo(lastIndex);
  };

  // j/k: closes any open thread first, then moves focus in the list.
  // Arrow keys use handleNavigateUp/Down instead, which navigate
  // between threads when one is open (without closing it).
  const moveFocus = (step: number) => {
    if (!allDataPages.value?.length) {
      return;
    }

    if (isOpen.value) {
      closeThread();
    }

    if (focusedIndex.value === -1) {
      focusedIndex.value = 0;
    } else {
      const requestedStep = focusedIndex.value + step;
      const newIndex =
        step > 0
          ? Math.min(allDataPages.value.length - 1, requestedStep)
          : Math.max(0, requestedStep);
      focusedIndex.value = newIndex;
    }

    scrollTo(focusedIndex.value);
  };

  // Arrow keys: navigate between open threads, or move focus if no thread is open.
  // Unlike moveFocus (j/k), these do NOT close the current thread.
  const handleNavigateUp = () => {
    if (isOpen.value) {
      incrementThread(-1);
    } else if (allDataPages.value?.length) {
      const newIndex = Math.max(0, focusedIndex.value - 1);
      focusedIndex.value = newIndex;
      scrollTo(newIndex);
    }
  };

  const handleNavigateDown = () => {
    if (isOpen.value) {
      incrementThread(1);
    } else if (allDataPages.value?.length) {
      const newIndex = Math.min(
        allDataPages.value.length - 1,
        focusedIndex.value + 1
      );
      focusedIndex.value = newIndex;
      scrollTo(newIndex);
    }
  };

  const handleOpenThread = () => {
    if (!isOpen.value && allDataPages.value?.length) {
      const thread =
        focusedIndex.value >= 0
          ? allDataPages.value[focusedIndex.value]
          : allDataPages.value[0];
      if (thread) {
        toggleThread(thread.id);
      }
    }
  };

  const handleEscapeAction = () => {
    const activeElement = document.activeElement;
    if (isActiveElementEditable(activeElement)) {
      (activeElement as HTMLElement).blur();
      return;
    }

    if (isOpen.value) {
      closeThread();
    }
  };

  assignShortcutToAction({
    actionName: ShortcutAction.InboxNextThread,
    action: () => moveFocus(1),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxPreviousThread,
    action: () => moveFocus(-1),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxCloseThread,
    action: handleEscapeAction,
    canPerformAction: canPerformEscapeAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxNavigateUp,
    action: handleNavigateUp,
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxNavigateDown,
    action: handleNavigateDown,
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxOpenThread,
    action: handleOpenThread,
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxJumpToTop,
    action: jumpToTop,
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxJumpToBottom,
    action: jumpToBottom,
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxGoToAllActivities,
    action: () => navigateToTab(),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxGoToEmails,
    action: () => navigateToTab(CommunicationType.email),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxGoToSms,
    action: () => navigateToTab(CommunicationType.sms),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxGoToCalls,
    action: () => navigateToTab(CommunicationType.call),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxGoToTasks,
    action: () => navigateToTab(CommunicationType.task),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxGoToNotes,
    action: () => navigateToTab(CommunicationType.note),
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxReply,
    action: () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_REPLY);
      }
    },
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxNextRecord,
    action: () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_NEXT_RECORD);
      }
    },
    canPerformAction: canPerformInboxAction,
  });

  assignShortcutToAction({
    actionName: ShortcutAction.InboxPreviousRecord,
    action: () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_PREVIOUS_RECORD);
      }
    },
    canPerformAction: canPerformInboxAction,
  });
};
